import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { MaskedLines, Reveal } from "../components/Reveal";
import EditorialMarquee from "../components/Marquee";
import Testimonials from "../components/Testimonials";
import FAQ from "../components/FAQ";
import { SPECIALTIES, SERVICES } from "../lib/data";

const HERO_IMG = "https://images.unsplash.com/photo-1540420773420-3366772f4999";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      ref={ref}
      className="relative min-h-screen pt-28 pb-16 md:pt-36 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end min-h-[80vh]">
        <div className="md:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-sans text-xs md:text-sm tracking-[0.4em] uppercase text-[#7D8C7A] mb-8"
          >
            Clinical & Integrative Nutrition
          </motion.p>

          <h1 className="font-serif text-[#2C3C30] text-[15vw] md:text-[8.5vw] leading-[0.86] tracking-tighter">
            <MaskedLines
              lines={["Healing", "starts in", <em key="e" className="text-[#7D8C7A]">the gut.</em>]}
              delay={0.3}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="font-sans text-lg md:text-xl text-[#566158] max-w-xl mt-10 leading-relaxed"
          >
            Evidence-based Medical Nutrition Therapy (treating health conditions
            through a proper diet plan) for IBS (a sensitive gut that cramps,
            bloats & causes irregular bowels), IBD (long-term gut inflammation,
            like Crohn&rsquo;s or colitis), GERD (acid reflux & heartburn), SIBO
            (bacterial overgrowth causing gas & bloating) and chronic bloating —
            combined with holistic gut restoration. A calmer gut, and a clear
            path forward.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="flex flex-wrap items-center gap-5 mt-12"
          >
            <Link
              to="/contact"
              data-testid="hero-book-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-[#2C3C30] px-8 py-4 text-[#F7F6F2] font-medium transition-colors duration-300 hover:bg-[#1A211D]"
            >
              Book a Consultation
              <ArrowUpRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </Link>
            <Link
              to="/services"
              data-testid="hero-services-link"
              className="font-sans text-sm uppercase tracking-wide text-[#2C3C30] border-b border-[#2C3C30]/40 pb-1 hover:border-[#2C3C30] transition-colors duration-300"
            >
              Explore Services
            </Link>
          </motion.div>
        </div>

        <div className="md:col-span-5 relative h-[420px] md:h-[70vh]">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ delay: 0.6, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 rounded-t-[10rem] overflow-hidden"
          >
            <motion.img
              src={HERO_IMG}
              alt="Fresh vibrant vegetables"
              style={{ y, scale }}
              className="w-full h-[130%] object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.4, duration: 0.7 }}
            className="absolute -bottom-6 -left-6 md:left-auto md:-right-6 bg-[#EAE7E0] border border-[#2C3C30]/10 rounded-2xl px-6 py-5 shadow-sm"
          >
            <p className="font-serif text-4xl text-[#2C3C30] leading-none">5+</p>
            <p className="font-sans text-xs uppercase tracking-widest text-[#566158] mt-1">
              Years in clinical practice
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ManifestoItem({ item }) {
  return (
    <div
      className="group relative border-b border-[#2C3C30]/15 py-8 md:py-12"
      data-testid={`specialty-item-${item.num}`}
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-10">
        <span className="font-sans text-sm text-[#7D8C7A] tabular-nums">
          ({item.num})
        </span>
        <h3 className="font-serif text-4xl md:text-6xl text-[#2C3C30] tracking-tight transition-transform duration-500 md:group-hover:translate-x-6">
          {item.title}
        </h3>
        <span className="md:ml-auto font-sans text-xs uppercase tracking-[0.25em] text-[#566158] border border-[#2C3C30]/20 rounded-full px-4 py-1.5 w-fit">
          {item.tag}
        </span>
      </div>
      <p className="font-sans text-[#566158] max-w-2xl mt-4 md:mt-6 md:pl-16 leading-relaxed">
        {item.blurb}
      </p>
    </div>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />

      {/* Intro statement */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-40">
        <Reveal>
          <p className="font-sans text-xs tracking-[0.4em] uppercase text-[#7D8C7A] mb-10">
            The Adya approach
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.15] tracking-tight text-[#2C3C30] max-w-5xl">
            You&rsquo;ve been told it&rsquo;s &ldquo;just stress.&rdquo; We think
            differently. We treat the root cause with{" "}
            <span className="text-[#7D8C7A] italic">
              evidence-based nutrition science
            </span>{" "}
            and holistic gut restoration — so healing feels possible again.
          </p>
        </Reveal>
      </section>

      <EditorialMarquee />

      {/* Specialties manifesto */}
      <section
        className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-40"
        data-testid="specialties-section"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 md:mb-20 gap-6">
          <Reveal>
            <h2 className="font-serif text-5xl md:text-7xl tracking-tight text-[#2C3C30]">
              Clinical Focus
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-sans text-[#566158] max-w-sm">
              Four areas of deep expertise, anchored by a gut-first philosophy of
              care.
            </p>
          </Reveal>
        </div>

        <div className="border-t border-[#2C3C30]/15">
          {SPECIALTIES.map((s) => (
            <Reveal key={s.num}>
              <ManifestoItem item={s} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* Services teaser */}
      <section className="bg-[#EAE7E0] py-24 md:py-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="font-sans text-xs tracking-[0.4em] uppercase text-[#7D8C7A] mb-6">
              How we work together
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 mt-8">
            {SERVICES.map((srv, i) => (
              <Reveal key={srv.num} delay={i * 0.1}>
                <div
                  className="bg-[#F7F6F2] rounded-2xl p-8 h-full border border-[#2C3C30]/10 transition-transform duration-500 hover:-translate-y-2"
                  data-testid={`service-card-${srv.num}`}
                >
                  <span className="font-serif text-3xl text-[#7D8C7A]">
                    {srv.num}
                  </span>
                  <h3 className="font-serif text-3xl text-[#2C3C30] mt-4 mb-3 tracking-tight">
                    {srv.title}
                  </h3>
                  <p className="font-sans text-[#566158] leading-relaxed">
                    {srv.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/services"
              data-testid="home-services-cta"
              className="group inline-flex items-center gap-3 mt-12 font-sans text-[#2C3C30] uppercase tracking-wide text-sm"
            >
              View all services
              <ArrowRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-2"
              />
            </Link>
          </Reveal>
        </div>
      </section>

      <Testimonials />
      <FAQ />
    </div>
  );
}
